import { Lock } from "lucide-react";
import { useCallback, useState } from "react";
import { useOutletContext, useParams } from "react-router";
import { Button } from "@/components/Button";
import { TextInput } from "@/components/TextInput";
import { Toast } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { useEntitySettings } from "@/lib/useEntitySettings";
import type { GatewayLayoutContext } from "./GatewayLayout";
import { LoadError } from "@/components/LoadError";

interface Gateway {
  id: string;
  name: string;
  source: string;
  publicUrl: string | null;
}

export function GatewaySettings() {
  const { orgId, gatewayId } = useParams();
  const { onGatewayRenamed } = useOutletContext<GatewayLayoutContext>();
  const api = useApi();
  const [publicUrl, setPublicUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const initializePublicUrl = useCallback((gateway: Gateway) => {
    setPublicUrl(gateway.publicUrl ?? "");
  }, []);
  const settings = useEntitySettings<Gateway>({
    entityId: gatewayId,
    endpointSegment: "gateways",
    entityLabel: "gateway",
    buildDeleteConfirmTitle: (name) => `Delete ${name}?`,
    buildDeleteConfirmMessage: () =>
      "Deleting this gateway will revoke its token and remove the route it provides to assigned resources.",
    deleteSuccessMessage: (name) => `${name} deleted.`,
    afterDeleteNavigateTo: `/${orgId}/networking/gateways`,
    onRenamed: onGatewayRenamed,
    onLoaded: initializePublicUrl,
  });

  const nameChanged = settings.entity
    ? settings.name !== settings.entity.name
    : false;
  const publicUrlChanged = settings.entity
    ? publicUrl !== (settings.entity.publicUrl ?? "")
    : false;

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();

    if (!orgId || !gatewayId) {
      return;
    }

    setSaving(true);

    try {
      if (publicUrlChanged) {
        await api.send(
          `/organizations/${orgId}/gateways/${gatewayId}/public-url`,
          "PATCH",
          { publicUrl },
          "Couldn't update gateway.",
        );
      }

      if (nameChanged) {
        await api.send(
          `/organizations/${orgId}/gateways/${gatewayId}`,
          "PATCH",
          { name: settings.name },
          "Couldn't update gateway.",
        );

        onGatewayRenamed(settings.name);
      }

      settings.showToast("Changes saved.", "success");
      await settings.refresh();
    } catch (err) {
      settings.showToast(
        err instanceof Error ? err.message : "Couldn't update gateway.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  }

  if (settings.error && !settings.entity) {
    return <LoadError what="this gateway" />;
  }

  if (!settings.entity) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-3 w-20 rounded bg-surface" />
        <div className="h-10 w-full rounded bg-surface" />
        <div className="h-3 w-24 rounded bg-surface" />
        <div className="h-10 w-full rounded bg-surface" />
      </div>
    );
  }

  const isHosted = settings.entity.source === "Hosted";

  return (
    <div className="space-y-6">
      {isHosted && (
        <div className="flex items-start gap-2.5 rounded-lg border border-muted p-3 text-sm text-foreground/70">
          <Lock
            className="mt-0.5 h-3.5 w-3.5 shrink-0"
            strokeWidth={2}
            aria-hidden="true"
          />
          <p>
            This gateway is hosted by Tendra. Its name and public URL are
            managed for you and it can't be deleted.
          </p>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label
            htmlFor="gatewayName"
            className="mb-1.5 block text-sm text-foreground"
          >
            Name
          </label>
          <TextInput
            id="gatewayName"
            value={settings.name}
            onChange={(e) => settings.setName(e.target.value)}
            required
            disabled={isHosted}
            className="w-full disabled:opacity-50"
          />
        </div>
        <div>
          <label
            htmlFor="gatewayPublicUrl"
            className="mb-1.5 block text-sm text-foreground"
          >
            Public URL
          </label>
          <TextInput
            id="gatewayPublicUrl"
            value={publicUrl}
            onChange={(e) => setPublicUrl(e.target.value)}
            required
            disabled={isHosted}
            className="w-full disabled:opacity-50"
            placeholder="https://gateway.example.com:2112"
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            disabled={isHosted || saving || (!nameChanged && !publicUrlChanged)}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>

      {!isHosted && (
        <div className="flex items-center justify-between gap-6 rounded-lg border border-danger/30 p-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Delete this gateway
            </h2>
            <p className="mt-0.5 max-w-md text-xs text-foreground/70">
              Deleting this gateway will revoke its token and remove the route
              it provides to assigned resources.
            </p>
          </div>
          <Button
            variant="dangerOutline"
            onClick={settings.handleDelete}
            disabled={saving || settings.submitting}
            className="shrink-0"
          >
            Delete gateway
          </Button>
        </div>
      )}

      <Toast toast={settings.toast} />

      {settings.confirmDialog}
    </div>
  );
}
